﻿import type { MediaAsset } from "../domain/types";
import { PHOTO_MAX, PHOTO_MIN } from "../media/limits";

export type Phase = "gate" | "verify" | "setup" | "profile";

export type StepId =
  | "welcome"
  | "faithConfirm"
  | "communityRules"
  | "intent"
  | "account"
  | "verifyIntro"
  | "verifyId"
  | "verifyVideo"
  | "verifyPhotos"
  | "verifyReview"
  | "setupIntro"
  | "whyChristian"
  | "valueScales"
  | "aboutYou"
  | "lookingFor"
  | "loveReady"
  | "lifestyle"
  | "faithDepth"
  | "character"
  | "history"
  | "appearance"
  | "homeAndLand"
  | "family"
  | "ancestry"
  | "interests"
  | "reflection"
  | "profile"
  | "ready";

export type ScaleKey =
  | "loyalty"
  | "honesty"
  | "passion"
  | "commitment"
  | "social"
  | "creativity"
  | "humor";

export interface FlowAnswers {
  /** Faith gate — must be true to proceed */
  isChristian: boolean | null;
  rules: {
    respectful: boolean;
    noSexualContent: boolean;
    noDominance: boolean;
  };
  intent: string | null;
  email: string;
  password: string;
  governmentId: MediaAsset | null;
  confirmationVideo: MediaAsset | null;
  profilePhotos: MediaAsset[];
  whyChristian: string;
  /** AGENTS.md: 1–10 honesty self-ratings */
  scales: Record<ScaleKey, number | null>;
  aboutYou: string;
  lookingFor: string;
  /** Must be true — “Are you ready to meet the love of your life?” */
  loveReady: boolean | null;
  hobbies: string;
  /** Optional free text — anything interesting about how they live. */
  lifestyleNotes: string;
  reading: number | null;
  profession: string;
  firstName: string;
  age: string;
  city: string;
  /** ISO country code (e.g. DE). Pre-filled from IP when available. */
  country: string;
  /**
   * Partner country preference for matching hard filter.
   * "same" | "any" | ISO code.
   */
  preferredPartnerCountry: "same" | "any" | string;
  /** Binary sex for matching — male | female only */
  gender: "male" | "female" | null;
}

export const PHASES: { id: Phase; label: string }[] = [
  { id: "gate", label: "Gate" },
  { id: "verify", label: "Verify" },
  { id: "setup", label: "Setup" },
  { id: "profile", label: "Profile" },
];

/**
 * SHORTENED 11 Sep 2026 (Masco: "as short and exciting as it should be, under 10").
 * Nine steps between the welcome screen and the finish line, down from sixteen.
 *
 * loveReady is now the THIRD thing anyone sees. "Are you ready to meet the love of
 * your life?" is the best question in this product and it sat at position fifteen,
 * behind a government ID upload.
 *
 * Out of the path: communityRules, whyChristian, lifestyle (free text, heavy to type,
 * little matching signal) and verifyIntro / verifyId / verifyVideo / verifyReview.
 * Nothing was deleted - the screens and canProceed still know them.
 *
 * READ BEFORE SHIPPING: government ID and the confirmation video are no longer
 * collected during onboarding. That is a change to the trust model, not just UX.
 * They must come back as a "verify before you can message" gate.
 */
export const STEP_ORDER: StepId[] = [
  "welcome",
  "faithConfirm",
  "loveReady",
  "intent",
  "account",
  "verifyPhotos",
  "aboutYou",
  "lookingFor",
  "valueScales",
  "profile",
  "ready",
];

export function phaseForStep(step: StepId): Phase | null {
  if (
    step === "welcome" ||
    step === "faithConfirm" ||
    step === "loveReady" ||
    step === "communityRules" ||
    step === "intent" ||
    step === "account"
  ) {
    return "gate";
  }
  if (
    step === "verifyIntro" ||
    step === "verifyId" ||
    step === "verifyVideo" ||
    step === "verifyPhotos" ||
    step === "verifyReview"
  ) {
    return "verify";
  }
  if (
    step === "setupIntro" ||
    step === "whyChristian" ||
    step === "valueScales" ||
    step === "aboutYou" ||
    step === "lookingFor" ||
    step === "lifestyle" ||
    step === "faithDepth" ||
    step === "character" ||
    step === "history" ||
    step === "appearance" ||
    step === "homeAndLand" ||
    step === "family" ||
    step === "ancestry" ||
    step === "interests" ||
    step === "reflection"
  ) {
    return "setup";
  }
  if (step === "profile" || step === "ready") return "profile";
  return null;
}

export { PHOTO_MIN, PHOTO_MAX };

export const COMMUNITY_RULES = [
  {
    key: "respectful" as const,
    title: "Be respectful",
    description: "Speak and act with dignity. Every person here is made in God’s image.",
  },
  {
    key: "noSexualContent" as const,
    title: "No sexual content",
    description: "No explicit photos, messages, or requests. Purity is not optional here.",
  },
  {
    key: "noDominance" as const,
    title: "Do not dominate or control",
    description: "No coercion, manipulation, or pressure. Love does not control.",
  },
] as const;

/** AGENTS.md mandatory 1–10 ratings. Social, creativity, and humor stay on the screen but never block Next. */
export const REQUIRED_SCALES: ScaleKey[] = [
  "loyalty",
  "honesty",
  "passion",
  "commitment",
];

export const VALUE_SCALES: {
  key: ScaleKey;
  label: string;
  hint: string;
}[] = [
  {
    key: "loyalty",
    label: "Level of loyalty",
    hint: "Be honest — how faithfully do you stand by those you love?",
  },
  {
    key: "honesty",
    label: "Level of honesty",
    hint: "How consistently do you tell the full truth?",
  },
  {
    key: "passion",
    label: "How passionate are you?",
    hint: "Conviction and depth — not performance.",
  },
  {
    key: "commitment",
    label: "Level of commitment",
    hint: "How ready are you to build something that lasts?",
  },
  {
    key: "social",
    label: "Social",
    hint: "Quiet evenings at one end. A full room at the other.",
  },
  {
    key: "creativity",
    label: "Creativity",
    hint: "How freely do you make and invent?",
  },
  {
    key: "humor",
    label: "Humor",
    hint: "How readily do you laugh — and make others laugh?",
  },
];

export const INTENT_OPTIONS = [
  {
    id: "marriage",
    label: "Marriage-minded",
    description: "I am seeking a partner for a lasting covenant.",
  },
  {
    id: "discernment",
    label: "Serious discernment",
    description: "I am open, prayerful, and unwilling to waste time.",
  },
  {
    id: "not-casual",
    label: "Not casual",
    description: "I am done with shallow connections and ambiguity.",
  },
] as const;

export const VERIFY_STEPS = [
  {
    id: "id",
    title: "Government ID",
    description: "Passport or national ID — camera or file upload.",
  },
  {
    id: "video",
    title: "Confirmation video",
    description: "A short recording that proves you are present.",
  },
  {
    id: "photos",
    title: "Real photos",
    description: "Seven to ten clear photos of yourself.",
  },
  {
    id: "review",
    title: "Integrity review",
    description: "Uploads stay pending_review until staff confirm.",
  },
] as const;

/** Text mins for free-text AGENTS fields */
export const TEXT_MIN = {
  whyChristian: 12,
  aboutYou: 20,
  lookingFor: 12,
  hobbies: 3,
  profession: 2,
  firstName: 2,
  city: 2,
  describeYourself: 40,
  lifePurpose: 20,
  fifteenYearVision: 20,
  weaknesses: 10,
} as const;

export function rulesAccepted(answers: FlowAnswers): boolean {
  return (
    answers.rules.respectful &&
    answers.rules.noSexualContent &&
    answers.rules.noDominance
  );
}

export function scalesComplete(answers: FlowAnswers): boolean {
  return REQUIRED_SCALES.every((key) => {
    const n = answers.scales[key];
    return typeof n === "number" && n >= 1 && n <= 10;
  });
}

export function isScale(n: number | null): n is number {
  return typeof n === "number" && n >= 1 && n <= 10;
}
